import {
  IAccount,
  IAccountService,
  IService,
  ProductType,
  ServiceType,
} from "@origin-digital/event-dispatcher";
import { BackendCrm } from "@origin-digital/platform-enums";
import { mapServiceType } from "./mapServiceType";
import { extractServiceCode } from "./extractServiceCode";

interface ServiceConfig {
  rank: number;
  accountMapper: (
    account: IAccount,
    propertyId?: string,
    multipleServicesEnabled?: boolean
  ) => IAccountService[];
  accountServiceIdentifier: (
    accountService?: IAccountService,
    multipleServicesEnabled?: boolean
  ) => string | undefined;
}

const isClosedAccount = (account: IAccount): boolean =>
  account.services.every(
    ({ serviceStatus }) =>
      serviceStatus === "CLOSED" || serviceStatus === "CANCELLED"
  );

/**
 * Return an IAccountService to service mapper for a given account.
 */
const defaultServiceMapper =
  (account: IAccount) =>
  (service: IService): IAccountService => ({
    originAccountId: account.id,
    accountId: account.backendAccountId,
    backendType: account.backend,
    serviceType: mapServiceType(account),
    serviceId: service.backendServiceId,
    productType: service.productType,
    isClosed: isClosedAccount(account),
  });

/**
 * Return an IAccountService for each service on the account.
 */
const defaultAccountMapper = (account: IAccount): IAccountService[] =>
  account.services.map(defaultServiceMapper(account));

const defaultAccountServiceIdentifier = (
  accountService?: IAccountService
): string | undefined => accountService?.serviceId;

/**
 * Configuration for each service selection type.
 * Includes the sorting rank and a mapper for transforming the account into one or more service selection objects.
 */
const SERVICE_TYPE_CONFIGURATION: Record<ServiceType, ServiceConfig> = {
  ELECTRICITY: {
    rank: 1,
    /**
     * There can be multiple electricity services, but ignore ESAAS products
     */
    accountMapper: (account: IAccount): IAccountService[] =>
      account.services.map(defaultServiceMapper(account)),
    accountServiceIdentifier: defaultAccountServiceIdentifier,
  },
  NATURAL_GAS: {
    rank: 2,
    /**
     * There can be multiple gas services
     */
    accountMapper: defaultAccountMapper,
    accountServiceIdentifier: defaultAccountServiceIdentifier,
  },
  /**
   *
   */
  HOT_WATER: {
    rank: 3,
    /**
     * Get the hot water service on the account, and ignore the others
     */
    accountMapper: (account) => {
      const hotWaterService = account.services.find(
        (service) => service.productType === ProductType.HOT_WATER
      );
      return hotWaterService
        ? [defaultServiceMapper(account)(hotWaterService)]
        : [];
    },
    accountServiceIdentifier: defaultAccountServiceIdentifier,
  },
  BROADBAND: {
    rank: 4,
    /**
     * Get services for the SUPERLOOP account and ignore the old BROADBAND account type.
     * When internetMultipleServicesPerProperty feature flag is enabled, return all BROADBAND services
     * for the selected property. Otherwise, return only the first service (legacy behavior).
     */
    accountMapper: (account, propertyId, multipleServicesEnabled = false) => {
      // Feature flag enabled: return all services for the selected property with extracted service codes
      if (multipleServicesEnabled) {
        if (account.backend !== BackendCrm.SUPERLOOP) {
          return [];
        }

        // Filter services by the selected property ID (same pattern as LPG)
        // Also filter out CLOSED/CANCELLED services to only show active ones
        const propertyServices = account.services.filter(
          (service) =>
            service.property.id === propertyId &&
            [ProductType.NBN, ProductType.OPTICOMM].includes(
              service.productType
            ) &&
            service.serviceStatus !== "CLOSED" &&
            service.serviceStatus !== "CANCELLED"
        );

        return propertyServices.map(
          (service): IAccountService => ({
            originAccountId: account.id,
            accountId: account.backendAccountId,
            backendType: account.backend,
            serviceType: ServiceType.BROADBAND,
            serviceId: extractServiceCode(service.backendServiceId),
            productType: service.productType,
            isClosed: isClosedAccount(account),
          })
        );
      }

      // Legacy behavior: original code untouched
      return account.backend === BackendCrm.SUPERLOOP
        ? [defaultServiceMapper(account)(account.services?.[0])]
        : [];
    },
    accountServiceIdentifier: defaultAccountServiceIdentifier,
  },
  LPG: {
    rank: 5,
    /**
     * Return a single account-service for one account, containing every service at the given property
     */
    accountMapper: (account, propertyId) => {
      const propertyServices = account.services.filter(
        (service) => service.property.id === propertyId
      );
      const services = propertyServices.map((service) => ({
        serviceId: service?.backendServiceId ?? "",
        productType: service?.productType ?? ProductType.UNKNOWN,
        isClosed:
          service?.serviceStatus === "CLOSED" ||
          service?.serviceStatus === "CANCELLED",
        reference: service.reference,
      }));
      return [
        {
          originAccountId: account.id,
          accountId: account.backendAccountId,
          backendType: account.backend,
          serviceType: ServiceType.LPG,
          isClosed: services.every((service) => service.isClosed),
          services,
        },
      ];
    },
    accountServiceIdentifier: (accountService?: IAccountService) =>
      accountService?.accountId,
  },
  /**
   * Include ALL accounts-services even if not displayed to user, they're required for account filtering by property
   */
  OHM: {
    rank: 6,
    accountMapper: defaultAccountMapper,
    accountServiceIdentifier: defaultAccountServiceIdentifier,
  },
  VPP_BATTERY: {
    rank: 7,
    accountMapper: defaultAccountMapper,
    accountServiceIdentifier: defaultAccountServiceIdentifier,
  },
  UNKNOWN: {
    rank: 8,
    accountMapper: defaultAccountMapper,
    accountServiceIdentifier: defaultAccountServiceIdentifier,
  },
};

/**
 * Remove any empty services or account-services.
 */
const cleanAccountServices = (
  accountServices: IAccountService[]
): IAccountService[] => {
  return accountServices
    .map((accountService) => ({
      ...accountService,
      services: accountService.services?.filter((service) => service.serviceId),
    }))
    .filter(
      (accountService) =>
        accountService.serviceId || accountService.services?.length
    );
};

const sortAccountServices = (a: IAccountService, b: IAccountService) => {
  if (
    SERVICE_TYPE_CONFIGURATION[a.serviceType].rank !==
    SERVICE_TYPE_CONFIGURATION[b.serviceType].rank
  ) {
    return (
      (SERVICE_TYPE_CONFIGURATION[a.serviceType].rank ?? Number.MAX_VALUE) -
      (SERVICE_TYPE_CONFIGURATION[b.serviceType].rank ?? Number.MAX_VALUE)
    );
  }
  if (a.isClosed !== b.isClosed) {
    return a.isClosed ? 1 : -1;
  }
  return a.accountId.localeCompare(b.accountId);
};

export const mapToAccountServices = (
  accounts: IAccount[],
  propertyId: string,
  multipleServicesEnabled: boolean
): IAccountService[] => {
  const result: IAccountService[] = [];
  accounts.forEach((account) => {
    const serviceType = mapServiceType(account);
    result.push(
      ...SERVICE_TYPE_CONFIGURATION[serviceType].accountMapper(
        account,
        propertyId,
        multipleServicesEnabled
      )
    );
  });
  return cleanAccountServices(result).sort(sortAccountServices);
};

export const getAccountServiceIdentifier = (
  accountService?: IAccountService,
  multipleServicesEnabled?: boolean
) => {
  if (!accountService) {
    return undefined;
  }
  return SERVICE_TYPE_CONFIGURATION[
    accountService.serviceType
  ].accountServiceIdentifier(accountService, multipleServicesEnabled);
};
